import React, { useEffect, useId, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';

interface QuantityPopoverProps {
    /** The field's label, e.g. "Antal (st)" */
    label: string;
    /** Hint lines under the label */
    hints: string[];
    /** The value the field starts from */
    initialValue: number | null | undefined;
    unitLabel: string;
    /** Whole pieces (kr/st): steps and starts at 1 */
    wholeNumbers: boolean;
    /** Shows Återställ (the row has an own value) */
    canReset: boolean;
    resetTitle: string;
    onSave: (value: number) => void;
    onReset: () => void;
    onClose: () => void;
    /** The button that opens the popover: a press on it is not an outside click */
    anchorRef: React.RefObject<HTMLElement | null>;
}

/**
 * The small card under a Mängd cell where a row's own quantity (m², lm) or
 * antal (st) is typed. It only feeds the row's cost, never the component.
 */
export const QuantityPopover: React.FC<QuantityPopoverProps> = ({
    label,
    hints,
    initialValue,
    unitLabel,
    wholeNumbers,
    canReset,
    resetTitle,
    onSave,
    onReset,
    onClose,
    anchorRef
}) => {
    const [inputValue, setInputValue] = useState(initialValue?.toString() ?? '');
    const popoverRef = useRef<HTMLDivElement>(null);
    const inputId = useId();

    // Close on a press outside the popover and its button
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, anchorRef]);

    const handleSave = () => {
        const value = parseFloat(inputValue);
        if (!isNaN(value) && value > 0 && value <= 1000000) {
            onSave(value);
            onClose();
        }
    };

    const handleReset = () => {
        onReset();
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    return (
        <div
            ref={popoverRef}
            className="card shadow-sm"
            style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                width: '270px',
                marginTop: '4px'
            }}
        >
            <div className="card-body p-3">
                <label htmlFor={inputId} className="form-label fw-semibold small mb-1">
                    {label}
                </label>
                {hints.map(hint => (
                    <div key={hint} className="mb-2 text-muted small">{hint}</div>
                ))}
                <div className="input-group input-group-sm mb-2">
                    <input
                        id={inputId}
                        type="number"
                        className="form-control"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        min={wholeNumbers ? '1' : '0.1'}
                        max="1000000"
                        step={wholeNumbers ? '1' : '0.1'}
                        autoFocus
                    />
                    <span className="input-group-text">{unitLabel}</span>
                </div>
                <div className="d-flex justify-content-between">
                    {canReset && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                            onClick={handleReset}
                            title={resetTitle}
                        >
                            <RotateCcw size={12} className="me-1" />
                            Återställ
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-primary btn-sm ms-auto"
                        onClick={handleSave}
                    >
                        Spara
                    </button>
                </div>
            </div>
        </div>
    );
};
